import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { PhotoAsset, PhotoArtifactTab } from '../../types.ts/story';

import { SidebarOption } from '../../types.ts/general';
import BackIcon from '../../svgs/BackIcon';
import { runInAction } from 'mobx';
import ChatGPTService from '../../services/ChatGPTService';
import SpinningLoading from '../SpinningLoading';
import { handleRemoveMedia } from '../../utility/general';
import FileListItem from '../common/FileListItem';
import { getImageWithBlackFrameElements } from '../../utility/elements';
import { trackDownload } from '../../utility/unsplash';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { useDatoClient, useStore } from '@src/stores-v2/StoreContext';
import { DEFAULT_ANIMATION_SPEED } from '@src/config/imageSettings';
import {
  useAddMediaToTimeline,
  MediaFileItem,
  MediaType,
} from '../../hooks/useAddMediaToTimeline';
import PhotoTabActions from './PhotoTabActions';
import PhotoGrid from './PhotoGrid';
import { usePhotoAssetManager } from '../../hooks/usePhotoAssetManager';
import { useAIPhotoGeneration } from '../../hooks/useAIPhotoGeneration';
import { useUnsplashSearch } from '../../hooks/useUnsplashSearch';
import { AspectRatio } from '../../types.ts/video';

export type Props = {
  selectedAssetFunction?: (asset: any) => void;
};

export const ArtifactsAndAssets: React.FC<Props> = observer((props) => {
  const videoCreator = useVideoCreatorStore();
  const datoClientStore = useDatoClient();
  const { editorStore } = useStore();
  const { addMediaToTimeline } = useAddMediaToTimeline();

  // Use our custom hooks
  const photoAssetManager = usePhotoAssetManager();
  const { generateAIPhotos, isGenerating: loadingAi } = useAIPhotoGeneration();
  const unsplashSearch = useUnsplashSearch();

  const { tab, resource, lastSelectedStock, lastSelectedAi, selectedId } =
    videoCreator.selectedPhotoAssets || {};
  const [savingAi, setSavingAi] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [isFileUploading, setIsFileUploading] = useState(false);
  const [uploadButtonActive, setUploadButtonActive] = useState(false);
  const [showBack, setShowBack] = useState(false);

  useEffect(() => {
    // Always update resources when the data sources change
    if (tab === PhotoArtifactTab.story) {
      videoCreator.selectedPhotoAssets = {
        tab: PhotoArtifactTab.story,
        resource: photoAssetManager.storyPhotos,
        lastSelectedStock,
        lastSelectedAi,
        selectedId,
      };
    } else if (tab === PhotoArtifactTab.album) {
      videoCreator.selectedPhotoAssets = {
        tab: PhotoArtifactTab.album,
        resource: photoAssetManager.albumPhotos,
        lastSelectedStock,
        lastSelectedAi,
        selectedId,
      };
    } else if (tab === PhotoArtifactTab.logo) {
      videoCreator.selectedPhotoAssets = {
        tab: PhotoArtifactTab.logo,
        resource: photoAssetManager.albumLogos,
        lastSelectedStock,
        lastSelectedAi,
        selectedId,
      };
    }
  }, [
    photoAssetManager.storyPhotos,
    photoAssetManager.albumPhotos,
    photoAssetManager.albumLogos,
    tab,
  ]);

  const handleSort = () => {
    photoAssetManager.handleSort();
    photoAssetManager.refreshAssetsForTab(tab);
  };

  const handleSelectResource = async (
    resource: PhotoAsset,
    tab: PhotoArtifactTab,
  ) => {
    if (props.selectedAssetFunction) {
      props.selectedAssetFunction(resource);
      return;
    }

    runInAction(() => {
      videoCreator.selectedPhotoAssets = {
        ...videoCreator.selectedPhotoAssets,
        selectedId: resource?.id,
      };
    });

    // Determine media type based on context
    let mediaType: 'video' | 'photo' | 'logo' = 'photo';

    // Check if this is a logo based on type or mediaOptions
    if (tab === PhotoArtifactTab.logo) {
      mediaType = 'logo';
    }
    const isPortrait = !!resource.aspectRatio && resource.aspectRatio < 1;

    // isVideoType
    //   ? {
    //     volume: 80,
    //     fit: 'cover' as const,
    //     autoFade: false,
    //   }
    const options = {
      photoDuration: 4,
      fit: mediaType === 'logo' ? ('contain' as const) : ('cover' as const),
      opacity: mediaType === 'logo' ? '40%' : undefined,
      tab: tab,
      searchValue: resource.type === 'ai' ? searchValue : undefined,
      useComposition: isPortrait,
      smartCrop: true,
    };

    const success = await addMediaToTimeline(
      { ...resource, description: resource.description || resource.title },
      mediaType,
      options,
    );

    if (tab === PhotoArtifactTab.stock) {
      unsplashSearch.clearSearch();
      videoCreator.selectedPhotoAssets = {
        tab: PhotoArtifactTab.story,
        resource: [],
      };
      trackDownload(resource?.attribution?.download_location);
    }

    videoCreator.sidebarOptions = SidebarOption.editing;
  };

  const handleMediaViaOriginal = async (
    resource: PhotoAsset,
    source: string,
  ) => {
    const freeTrack = videoCreator.getFreeMediaTrack('image', 4);
    const isPortrait = !!resource.aspectRatio && resource.aspectRatio < 1;
    const animations = [
      {
        start_scale: '100%',
        end_scale: DEFAULT_ANIMATION_SPEED,
        x_anchor: '50%',
        y_anchor: '50%',
        fade: false,
        scope: 'element',
        easing: 'linear',
        type: 'scale',
        arbor_subType: 'zoomIn',
      },
    ];
    const element =
      isPortrait &&
      videoCreator.currentVideo?.aspectRatio === AspectRatio.AR_16_9
        ? {
            type: 'composition',
            duration: '4 s',
            ...(freeTrack && { track: freeTrack }),
            source: null,
            elements: getImageWithBlackFrameElements({ source }),
            animations,
          }
        : {
            type: 'image',
            source,
            duration: '4 s',
            ...(freeTrack && { track: freeTrack }),
            autoplay: true,
            fit: 'cover',
            smart_crop: true,
            animations,
          };
    await videoCreator.createElement(element);
  };

  const handleSelectTab = async (tab: PhotoArtifactTab) => {
    let resource: PhotoAsset[] | undefined = undefined;
    switch (tab) {
      case PhotoArtifactTab.story:
        resource = photoAssetManager.storyPhotos;
        break;
      case PhotoArtifactTab.album:
        resource = photoAssetManager.albumPhotos;
        break;
      case PhotoArtifactTab.logo:
        resource = photoAssetManager.albumLogos;
        break;
      case PhotoArtifactTab.stock:
        resource = lastSelectedStock?.length ? lastSelectedStock : undefined;
        break;
      case PhotoArtifactTab.ai:
        resource = lastSelectedAi?.length ? lastSelectedAi : undefined;
        break;
    }
    setShowBack(false);
    videoCreator.selectedPhotoAssets = {
      tab,
      resource,
      lastSelectedStock,
      lastSelectedAi,
      selectedId,
    };
  };

  const handleSearchArtifacts = (inputString: string) => {
    photoAssetManager.handleSearchArtifacts(inputString, tab);
    photoAssetManager.refreshAssetsForTab(tab);
  };

  const handleSearchAssets = async (textValue: string) => {
    await unsplashSearch.handleSearch(textValue);
    if (textValue) {
      videoCreator.selectedPhotoAssets = {
        tab: PhotoArtifactTab.stock,
        resource: unsplashSearch.results,
        lastSelectedStock: unsplashSearch.results,
        lastSelectedAi,
        selectedId,
      };
      setShowBack(true);
    }
  };

  useEffect(() => {
    if (unsplashSearch.results.length > 0) {
      videoCreator.selectedPhotoAssets = {
        tab: PhotoArtifactTab.stock,
        resource: unsplashSearch.results,
        lastSelectedStock: unsplashSearch.results,
        lastSelectedAi,
        selectedId,
      };
    }
  }, [unsplashSearch.results]);

  const handleSearchAi = async (textValue: string) => {
    if (!textValue.trim()) return;
    try {
      setSearchValue(textValue);
      const aiPhotos = await generateAIPhotos(textValue);

      videoCreator.selectedPhotoAssets = {
        tab: PhotoArtifactTab.ai,
        resource: aiPhotos,
        lastSelectedStock,
        lastSelectedAi: aiPhotos,
        selectedId,
      };
      setShowBack(true);
    } catch (error) {
      videoCreator.toastState = {
        state: 'error',
        message: 'Failed to generate AI photos. Please try again.',
      };
    }
  };

  const handleRegenerateTitle = async (
    id: string,
    tab: PhotoArtifactTab.story | PhotoArtifactTab.album,
  ) => {
    let storyId, organizationId;
    if (tab === PhotoArtifactTab.story) {
      storyId = videoCreator.story!.id;
      videoCreator.statusUpdateManager.addStoryArtifactsToWatch(
        videoCreator.story!.id,
        videoCreator.story!.storyArtifacts!.filter((a) => a.id === id),
      );
    } else if (tab === PhotoArtifactTab.album) {
      organizationId = videoCreator.organization!.id;
      videoCreator.statusUpdateManager.addOrganizationArtifactsToWatch(
        videoCreator.organization!.id,
        videoCreator.organization!.organizationArtifacts!.filter(
          (a) => a.id === id,
        ),
      );
    }
    try {
      const gptService = new ChatGPTService(
        videoCreator,
        datoClientStore.aiPromptRepository,
      );
      await gptService.regenerateArtifactTitle({
        artifactId: id,
        storyId,
        organizationId,
      });
    } catch (error) {
      console.error('Error occurred while regenerating title: ', error);
      videoCreator.toastState = {
        state: 'error',
        message: 'Could not regenerate description',
      };
    }
  };

  const handleUploadCallback = async () => {
    // Merge deleted in PLAT-4056 code that was moved in headless migration.
    // photoAssetManager.refreshAssetsForTab(tab);
  };

  const handleUploadClick = () => {
    setUploadButtonActive(true);
    setTimeout(() => setUploadButtonActive(false), 3000);
  };

  return (
    <Main>
      <Tabs>
        {Object.entries(PhotoArtifactTab).map(([k, v]) => (
          <Tab
            key={k}
            isSelected={tab === v}
            onClick={() => handleSelectTab(v)}
          >
            {v}
          </Tab>
        ))}
      </Tabs>

      <PhotoTabActions
        tab={tab}
        sortBy={photoAssetManager.sortBy}
        uploadButtonActive={uploadButtonActive}
        onSearch={
          tab === PhotoArtifactTab.stock
            ? handleSearchAssets
            : tab === PhotoArtifactTab.ai
              ? handleSearchAi
              : handleSearchArtifacts
        }
        onSort={handleSort}
        onUploadClick={handleUploadClick}
        onUploadCallback={handleUploadCallback}
        setIsLoading={setIsFileUploading}
      />

      {showBack && (
        <Back
          onClick={() => {
            setShowBack(false);
            if (tab === PhotoArtifactTab.stock) {
              unsplashSearch.clearSearch();
            }
            videoCreator.selectedPhotoAssets = {
              tab: PhotoArtifactTab.story,
              resource: photoAssetManager.storyPhotos,
              lastSelectedStock,
            };
          }}
        >
          <BackIcon />
        </Back>
      )}

      {(loadingAi || savingAi || isFileUploading) && (
        <SpinningLoading
          positionTop="20px"
          text={
            loadingAi
              ? 'Generating Ai photos'
              : savingAi
                ? 'Saving Ai photo'
                : isFileUploading
                  ? 'Saving media'
                  : null
          }
        />
      )}

      {tab === PhotoArtifactTab.story ||
      tab === PhotoArtifactTab.album ||
      tab === PhotoArtifactTab.logo ? (
        <PhotoContent>
          {videoCreator.selectedPhotoAssets.resource?.map((r) => (
            <>
              <FileListItem
                key={r.id}
                type={(() => {
                  if (tab === PhotoArtifactTab.story) {
                    return 'storyArtifacts';
                  }
                  if (tab === PhotoArtifactTab.logo) {
                    return 'organizationLogos';
                  }
                  return 'organizationArtifacts';
                })()}
                item={{
                  id: r.id,
                  description: r.description || r.title,
                  src: r.src!,
                  title: r.title,
                  aspectRatio: r.aspectRatio,
                  alt: r.alt,
                  slug: r.slug,
                  type: r.type,
                  attribution: r.attribution,
                }}
                mediaOptions={{
                  tab,
                  searchValue: r.type === 'ai' ? searchValue : undefined,
                  isLogo: tab === PhotoArtifactTab.logo,
                }}
                handleClick={() => handleSelectResource(r, tab)} //TODO
                isSelected={selectedId === r.id}
                handleRemove={() =>
                  handleRemoveMedia(
                    videoCreator,
                    datoClientStore.albumRepository,
                    r.id,
                    tab,
                    undefined,
                    photoAssetManager.sortBy,
                  )
                }
                handleRegenerateTitle={
                  tab === PhotoArtifactTab.story ||
                  tab === PhotoArtifactTab.album
                    ? () => handleRegenerateTitle(r.id, tab)
                    : undefined
                }
                callback={() => {
                  photoAssetManager.refreshAssetsForTab(tab);
                }}
              />
            </>
          ))}
        </PhotoContent>
      ) : (
        // stock or ai search tab
        <PhotoGrid
          resource={resource}
          tab={tab}
          selectedId={selectedId}
          unsplashSearch={unsplashSearch.searchTerm}
          unsplashLoading={unsplashSearch.isLoading}
          onSelectResource={handleSelectResource}
          onLoadMore={unsplashSearch.loadMorePhotos}
        />
      )}
    </Main>
  );
});
export default ArtifactsAndAssets;
const Main = styled.div`
  margin-top: 5px;
`;
const Back = styled.button`
  outline: 0;
  border: 0;
  cursor: pointer;
  background-color: #030419;
  margin-top: 16px;
`;

const PhotoContent = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
const Tab = styled.div<{ isSelected?: boolean }>`
  color: ${(props) => (props.isSelected ? '#F2D093' : '')};
  text-decoration: ${(props) => (props.isSelected ? 'underline' : '')};
  cursor: pointer;
  font-size: 14px;
  padding: 10px 5px 10px 0;
`;
const Tabs = styled.div<{ isSelected?: boolean }>`
  display: flex;
  gap: 16px;
  margin-left: 1px;
`;
